/**
 * The firm's Case Preparation defaults, read off the law firm record (and
 * the Exhibit Titles style in Firm Instructions): the exhibit title template, the label style new cases start with, and its
 * citation style. Nothing here is per case; a card's "For This Case Only"
 * note is the per-case override.
 */
import {
  casePrepValue,
  type CitationStyle,
  type LawFirm
} from "@/api/types/lawFirm";
import {
  BUILT_IN_TEMPLATE,
  citationExample,
  DEFAULT_LABEL_STYLE,
  firmSchemeOf,
  labelStyleOf
} from "@/components/settings/FirmHouseStyleSection";

import { matchPreset } from "../../caseFiles/exhibitStylePresets";
import {
  DEFAULT_EXHIBIT_SCHEME,
  type ExhibitScheme
} from "../../caseFiles/types";
import type { DefaultTemplate } from "./defaultTemplates";
import { packetContentsOf, packetOrderOf } from "./packetContents";
import { packetSettingsOf, packetStyleOf } from "./packetStyle";
import type { WorkflowSettings } from "./types";

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The firm's stored scheme as a case scheme, or null when it has none. */
export const firmScheme = firmSchemeOf;

export { DEFAULT_LABEL_STYLE, labelStyleOf };

/** The case's style when it isn't the firm's (null when they match). */
export const caseStyleDifference = (
  caseScheme: ExhibitScheme,
  firm: ExhibitScheme | null
): string | null => {
  if (!firm) return null;
  const a = matchPreset(caseScheme);
  const b = matchPreset(firm);
  if (a && b && a.id === b.id) return null;
  return labelStyleOf(caseScheme);
};

/**
 * What the workflows use: the firm's saved Case Preparation values, falling
 * back to CaseBlink's built-ins (the built-in title template, the case's own
 * label style or the new-case default, and "(Exhibit 12)" citations).
 */
export const workflowSettings = (
  firm: LawFirm | null,
  visa: string,
  caseScheme: ExhibitScheme = DEFAULT_EXHIBIT_SCHEME,
  defaultTemplates: DefaultTemplate[] = []
): WorkflowSettings => {
  const template = firm?.default_title_template?.default?.trim() ?? "";
  const style = casePrepValue(firm, "titleInstructions")?.trim() || null;
  const packet = casePrepValue(firm, "packetSettings");
  const scheme = firmScheme(firm?.default_exhibit_scheme);
  const citation = isRecord(firm?.default_exhibit_scheme?.citation_style)
    ? (firm?.default_exhibit_scheme?.citation_style as CitationStyle)
    : null;
  const defaultPreset = matchPreset(DEFAULT_EXHIBIT_SCHEME);
  const caseIsDefault =
    defaultPreset !== null && matchPreset(caseScheme)?.id === defaultPreset.id;
  const labelSource = scheme ? "firm" : caseIsDefault ? "default" : "case";
  return {
    visa,
    titleTemplate: template || BUILT_IN_TEMPLATE,
    titleTemplateIsFirm: Boolean(template || style),
    titleInstructions: style,
    packetSettings: packet
      ? packetSettingsOf(
          packetStyleOf(packet),
          packetContentsOf(packet),
          packetOrderOf(packet)
        )
      : null,
    labelStyle: labelStyleOf(scheme ?? caseScheme) ?? DEFAULT_LABEL_STYLE,
    labelSource,
    citation: citationExample(citation),
    citationIsFirm: citation !== null,
    askEveryWrite: Boolean(firm?.agent_ask_every_write),
    defaultTemplates
  };
};
